import { useState, useEffect } from 'react'
import { supabase } from './lib/supabase'
import Dashboard from './components/Dashboard'
import Header from './components/Header'
import LandingPage from './components/LandingPage'
import LoginPage from './components/LoginPage'
import ErrorBoundary from './components/ErrorBoundary'
import ChatLauncher from './components/ChatLauncher'
import ResetPasswordPage from './components/ResetPasswordPage'

type AppState = 'landing' | 'login' | 'dashboard' | 'reset'

function App() {
  const [currentState, setCurrentState] = useState<AppState>('landing')
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [chatVisible, setChatVisible] = useState(false)

  // Check Supabase session on load and subscribe to changes
  useEffect(() => {
    // Route simple pour la page de reset
    if (window.location.pathname.startsWith('/auth/reset')) {
      setCurrentState('reset')
      return
    }
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setCurrentState('dashboard')
        setUserEmail(data.session.user.email ?? null)
      }
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentState(session ? 'dashboard' : 'landing')
      setUserEmail(session?.user?.email ?? null)
    })
    return () => sub.subscription.unsubscribe()
  }, [])

  const handleLoginClick = () => {
    setCurrentState('login')
  }

  const handleLoginSuccess = () => {
    setCurrentState('dashboard')
  }

  const handleBackToLanding = () => {
    setCurrentState('landing')
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setCurrentState('landing')
  }

  return (
    <ErrorBoundary>
      {currentState === 'reset' && (
        <ResetPasswordPage onDone={() => setCurrentState('login')} />
      )}
      {currentState === 'landing' && (
        <>
          <Header onLogin={handleLoginClick} />
          <LandingPage onLoginClick={handleLoginClick} />
        </>
      )}
      
      {currentState === 'login' && (
        <LoginPage 
          onLoginSuccess={handleLoginSuccess}
          onBackClick={handleBackToLanding}
        />
      )}
      
      {currentState === 'dashboard' && (
        <div className="min-h-screen bg-gray-50">
          <Header onLogout={handleLogout} userLabel={userEmail ?? undefined} />
          <div className="flex h-[calc(100vh-88px)]">
            <main className="flex-1 overflow-auto px-4 py-8 pr-2 sm:pr-4 lg:pr-6">
              <div className="max-w-none mx-auto">
                <Dashboard />
              </div>
            </main>

            {/* Desktop Chat - Always visible on lg+ screens */}
            <aside className="hidden lg:block w-[420px] xl:w-[480px] 2xl:w-[520px] flex-shrink-0 border-l border-gray-200 bg-white">
              <ChatLauncher />
            </aside>

            {/* Mobile Chat Toggle Button */}
            <button
              onClick={() => setChatVisible(!chatVisible)}
              className="lg:hidden fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
              aria-label="Toggle chat"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.955 8.955 0 01-4.126-.964L3 21l1.964-5.874A8.955 8.955 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
              </svg>
            </button>

            {/* Mobile Chat Overlay */}
            {chatVisible && (
              <div className="lg:hidden fixed inset-0 z-40 flex">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setChatVisible(false)} />
                <aside className="relative ml-auto w-full max-w-sm bg-white h-full">
                  <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-lg font-semibold">Assistant</h2>
                    <button
                      onClick={() => setChatVisible(false)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="h-[calc(100%-64px)]">
                    <ChatLauncher />
                  </div>
                </aside>
              </div>
            )}
          </div>
        </div>
      )}
    </ErrorBoundary>
  )
}

export default App
